import { EmberTutorialPrompt } from "game-common/models"
import { Container, Graphics, Rectangle, Text } from "pixi.js"
import { TextRenderer } from "../text/text_renderer"
import { createButton } from "./button_factory"
import { UiButton } from "./ui_button"

const FADE_MS = 450

/** A full viewport title screen. The server releases the player after the outgoing fade. */
export class ChapterAnnouncement extends Container {
    private backdrop = new Graphics()
    private content = new Container()
    private chapter?: number
    private fadingOut = false
    private button?: UiButton
    private laterButton?: UiButton
    private dismissal: "begin" | "later" = "begin"
    private bounds = new Rectangle(0, 0, 800, 600)

    constructor(private onBegin: (chapter: number) => void, private canBegin: () => boolean = () => true, private onLater?: (chapter: number) => void) {
        super()
        this.addChild(this.backdrop, this.content)
        this.interactive = true
        this.on("pointerdown", (event: any) => event.stopPropagation())
        this.on("pointermove", (event: any) => event.stopPropagation())
        this.on("rightclick", (event: any) => event.stopPropagation())
        this.visible = false
    }

    open = (prompt: EmberTutorialPrompt) => {
        // Login and camp entry can both announce the same chapter. Preserve its current fade.
        if (this.visible && this.chapter === prompt.announcementChapter) return
        this.content.removeChildren().forEach(child => child.destroy({ children: true }))
        this.chapter = prompt.announcementChapter
        this.fadingOut = false
        this.laterButton = undefined
        this.dismissal = "begin"
        // The cover is opaque on the very first frame, before room loading can reveal the world.
        // Fade the chapter copy in over it; fade the whole cover out when Begin is pressed.
        this.alpha = 1
        this.content.alpha = 0
        this.visible = true
        const overline = new TextRenderer().role("title").color(0xf2a94e).strokeThickness(0)
            .render(`Chapter ${this.chapter}`) as Text
        const title = new TextRenderer().role("display").color(0xfff3e0).strokeThickness(0)
            .wordWrap(560).render(prompt.title) as Text
        const description = new TextRenderer().role("body").color(0xc9bba7).strokeThickness(0)
            .wordWrap(480).render(prompt.body) as Text
        for (const text of [overline, title, description]) {
            text.anchor.set(0.5, 0)
            text.style.align = "center"
            this.content.addChild(text)
        }
        overline.y = 0
        title.y = 46
        description.y = title.y + Math.max(title.height, 48) + 24
        this.button = createButton({ label: "Begin Chapter", theme: "ember", preset: "primary",
            width: 168, height: 36, size: "medium", bold: true, onClick: this.begin })
        this.button.position.set(-84, description.y + Math.max(description.height, 40) + 44)
        this.content.addChild(this.button)
        if ((this.chapter || 1) >= 2) {
            this.laterButton = createButton({ label: "Maybe later", theme: "ember", variant: "secondary",
                width: 168, height: 36, size: "medium", onClick: this.maybeLater })
            this.content.addChild(this.laterButton)
        }
        this.resize(this.bounds.width, this.bounds.height, this.bounds.x, this.bounds.y)
    }

    resize = (width: number, height: number, x = 0, y = 0) => {
        this.bounds = new Rectangle(x, y, width, height)
        this.hitArea = this.bounds
        this.backdrop.clear().beginFill(0x100e0c, 1).drawRect(x, y, width, height).endFill()
        const texts = this.content.children.filter(child => child instanceof Text) as Text[]
        texts.forEach(text => { text.style.wordWrapWidth = Math.min(560, Math.max(160, width - 48)) })
        if (texts.length === 3 && this.button) {
            texts[1].y = 46
            texts[2].y = texts[1].y + Math.max(texts[1].height, 48) + 24
            this.button.y = texts[2].y + Math.max(texts[2].height, 40) + 44
            const stacked = width < 400
            this.button.x = this.laterButton && !stacked ? 6 : -84
            if (this.laterButton) this.laterButton.position.set(stacked ? -84 : -174,
                this.button.y + (stacked ? 48 : 0))
        }
        this.content.scale.set(1)
        const naturalHeight = this.content.height
        const fit = Math.min(1, Math.max(0.2, (height - 48) / Math.max(1, naturalHeight)))
        this.content.scale.set(fit)
        this.content.x = x + width / 2
        this.content.y = y + Math.max(24, (height - this.content.height) / 2)
    }

    isOpen = (): boolean => this.visible

    /** Begin is deliberately unavailable until the incoming fade has finished. */
    begin = () => {
        if (!this.visible || this.fadingOut || this.content.alpha < 1 || !this.canBegin()) return
        this.dismissal = "begin"
        this.fadingOut = true
    }

    maybeLater = () => {
        if (!this.visible || this.fadingOut || this.content.alpha < 1 || (this.chapter || 1) < 2 || !this.canBegin()) return
        this.dismissal = "later"
        this.fadingOut = true
    }

    close = () => {
        this.chapter = undefined
        this.visible = false
        this.fadingOut = false
    }

    animate = (dtMs: number) => {
        if (!this.visible) return
        const step = Math.max(0, dtMs || 0) / FADE_MS
        if (this.fadingOut) this.alpha = Math.max(0, this.alpha - step)
        else this.content.alpha = Math.min(1, this.content.alpha + step)
        if (this.fadingOut && this.alpha === 0) {
            const chapter = this.chapter
            this.close()
            if (chapter !== undefined) {
                if (this.dismissal === "later") this.onLater?.(chapter)
                else this.onBegin(chapter)
            }
        }
    }
}
